import { Link } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { useCanState } from '@/hooks/useEmployeeRights';

export type EntityKind = 'insurer' | 'contact' | 'channel' | 'posp' | 'broker' | 'policy' | 'quote';

/**
 * W4m: the ROLE each kind's route admits, mirroring `<Route>` in App.tsx.
 *
 * A link is a promise that the destination will open. Before this table only
 * `posp` kept it: the other six branches handed every viewer a link their own
 * route would bounce them from -- most visibly the insurer link on the
 * policies list (`PoliciesListPage.tsx:742`), which is broker-admin-only and
 * was rendered for employees, POSPs and customers alike.
 *
 * ROLE ONLY, deliberately. The rights cell is checked for `posp` and nowhere
 * else, because putting a rights hook in this component's common path couples
 * every list in the app to the rights query -- it broke every test that
 * partially mocks `useEmployeeRights`, and it would make an ordinary contact
 * link depend on a network answer. Where the two would disagree it does not
 * matter anyway: `insurer` and `channel` admit only `broker_admin`, who holds
 * every cell by role. The residual gap is a gated role whose module read is
 * revoked (say an employee without `contacts: read`) still seeing a contact
 * link; that is a dead link, not an exposure, and the page behind it shows the
 * real refusal. Closing it properly means the PAGE passing its already-loaded
 * rights answer down as a prop, not this component fetching one per link.
 */
const ROUTE_ROLES: Record<EntityKind, readonly string[]> = {
  insurer: ['broker_admin'],
  contact: ['broker_admin', 'broker_employee', 'posp', 'super_posp'],
  channel: ['broker_admin'],
  // '/posps/:id' is a redirect-only resolver (LegacyPospRedirect): broker roles
  // bounce to /broker/channels/:channelId, posp/super_posp to /portal/:id.
  // broker_employee was dropped by D2.
  posp: ['broker_admin', 'posp', 'super_posp'],
  broker: ['super_admin', 'country_admin'],
  policy: [
    'country_admin',
    'broker_admin',
    'broker_employee',
    'posp',
    'super_posp',
    'insurer_admin',
    'insurer_employee',
    'customer',
  ],
  quote: ['broker_admin', 'broker_employee', 'posp', 'super_posp', 'insurer_admin', 'insurer_employee'],
};

function pathFor(kind: EntityKind, id: string): string | null {
  switch (kind) {
    case 'insurer': return `/broker/insurers/${id}/commissions`;
    case 'contact': return `/contacts/${id}`;
    case 'channel': return `/broker/channels/${id}`;
    case 'posp':    return `/posps/${id}`;
    case 'broker':  return `/brokers/${id}`;
    case 'policy':  return `/policies/${id}`;
    // `/quotes/:id` expects a QuoteRequest id (there's no standalone
    // per-insurer Quote detail page) -- round-2 Wave F3, item 10.
    case 'quote':   return `/quotes/${id}`;
    default:        return null;
  }
}

export function getEntityHref(
  kind: EntityKind,
  opts: {
    id?: string;
    slug?: string;
    viewerRole?: string | null;
    /** `posp` only: the `channels: read` answer. `false` is "checked, and
     * refused"; `undefined` means the caller did not check. */
    canReadChannels?: boolean;
  }
): string | null {
  const { id, viewerRole } = opts;
  if (!id) return null;
  const href = pathFor(kind, id);
  if (!href) return null;

  // `viewerRole === undefined` means the caller did not say -- treated as "not
  // gating here" so direct, role-blind callers of this pure function keep their
  // behaviour. `EntityLink` below always passes one, and an explicit `null`
  // (nobody signed in) IS gated.
  const roles = ROUTE_ROLES[kind];
  if (roles && viewerRole !== undefined && !roles.includes(viewerRole ?? '')) return null;

  if (kind === 'posp' && opts.canReadChannels === false) return null;
  return href;
}

interface EntityLinkProps {
  kind: EntityKind;
  id?: string | null;
  slug?: string | null;
  name: string;
  className?: string;
}

export function EntityLink({ kind, id, slug, name, className }: EntityLinkProps) {
  const viewerRole = useAuthStore((s) => s.user?.role ?? null);
  // Only the posp branch needs a rights answer, and it is the only branch that
  // asks for one: a `useCanState` call up here would mount the rights query
  // under every insurer/contact/policy link on every list in the app.
  if (kind === 'posp') {
    return (
      <PospEntityLink id={id ?? undefined} name={name} className={className} viewerRole={viewerRole} />
    );
  }
  const href = getEntityHref(kind, { id: id ?? undefined, slug: slug ?? undefined, viewerRole });
  if (!href) return <span className={className}>{name}</span>;
  return (
    <Link to={href} className={className ?? 'text-primary-600 hover:underline dark:text-primary-400'}>
      {name}
    </Link>
  );
}

/**
 * `/posps/:id` is guarded by a role list AND `channels: read`, so the link has
 * to satisfy both or it is a link to /unauthorized.
 *
 * Fails closed on purpose: while the rights answer is loading, or if it failed,
 * the name renders as plain text rather than as a link that may bounce. Nothing
 * is hidden by that -- the POSP's name is still on screen, and the destination
 * page shows the real "we could not check your access" state if reached
 * directly (ProtectedRoute's RightsGate).
 */
function PospEntityLink({
  id,
  name,
  className,
  viewerRole,
}: {
  id?: string;
  name: string;
  className?: string;
  viewerRole: string | null;
}) {
  const { allowed: canReadChannels, isError: rightsCheckFailed, retry } = useCanState(
    'channels',
    'read'
  );
  const href = getEntityHref('posp', { id, viewerRole, canReadChannels });
  if (!href) {
    // Both cases render plain text -- the link fails closed either way -- but
    // they are not the same fact. If the role list admits this viewer and the
    // only missing piece is a rights answer that never arrived, dropping the
    // link silently leaves the reader with no idea why a name that is usually
    // clickable no longer is, and no way to fix it.
    const roleAdmitsViewer =
      getEntityHref('posp', { id, viewerRole, canReadChannels: true }) !== null;
    if (rightsCheckFailed && roleAdmitsViewer) {
      return (
        <span className={className}>
          {name}{' '}
          <button
            type="button"
            onClick={() => retry?.()}
            title="Permissions could not be loaded — this is a connection problem, not a refusal"
            className="text-xs underline text-amber-700 dark:text-amber-500"
          >
            permissions unavailable — retry
          </button>
        </span>
      );
    }
    return <span className={className}>{name}</span>;
  }
  return (
    <Link to={href} className={className ?? 'text-primary-600 hover:underline dark:text-primary-400'}>
      {name}
    </Link>
  );
}
